import { bind } from 'lodash-decorators';
import { makeAutoObservable, observable } from 'mobx';
import { component, initialize } from 'tsdi';
import { LanguageOption, TLanguage } from '@components/language-select';
import { I18NText } from '@api/ChallengeClient';
import { LocalDate, ZonedDateTime } from '@ms-rewards/date-lib';

type TranslateFn = (
    key: I18nKey | null | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...params: any[]
) => string;

@component
export class I18n {
    public currentLocale = navigator.language;

    @bind
    public formatNumber(num: number) {
        const formatter = new Intl.NumberFormat(this.currentLocale, {});

        return formatter.format(num);
    }

    public formatYearMonth(yearMonth: string) {
        const formatter = new Intl.DateTimeFormat(this.currentLocale, {
            month: 'long'
        });
        const [year, month] = yearMonth.split('-');
        const monthFormatted = formatter.format(
            new Date(1970, Number(month) - 1)
        );

        return `${monthFormatted} ${year}`;
    }

    public formatDate = (date?: string) => {
        if (!date) {
            return '-';
        }

        const formatter = this.dateFormatter;

        return formatter.format(new Date(date));
    };

    private get dateFormatter(): Intl.DateTimeFormat {
        return new Intl.DateTimeFormat(this.currentLocale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }

    public formatDateTime = (date?: string) => {
        if (!date) {
            return '-';
        }

        const formatter = this.dateTimeFormat;

        return formatter.format(new Date(date));
    };

    private get dateTimeFormat(): Intl.DateTimeFormat {
        return new Intl.DateTimeFormat(this.currentLocale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric'
        });
    }

    public formatLongDate = (date?: string): string => {
        if (!date) {
            return '-';
        }

        const formatter = this.longDateFormat;

        return formatter.format(new Date(date));
    };

    private get longDateFormat(): Intl.DateTimeFormat {
        return new Intl.DateTimeFormat(this.currentLocale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour12: false
        });
    }

    public formatShortDate = (date?: string): string => {
        if (!date) {
            return '-';
        }

        const formatter = this.shortDateFormat;

        return formatter.format(new Date(date));
    };

    private get shortDateFormat(): Intl.DateTimeFormat {
        return new Intl.DateTimeFormat(this.currentLocale, {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour12: false
        });
    }

    public __: TranslateFn = () => '';

    @initialize
    protected init(): void {
        makeAutoObservable(this, { __: observable, formatNumber: false });
    }

    static mainLanguageKey: TLanguage = 'en';

    static allLanguageOptions: LanguageOption[] = [
        { label: 'English', value: 'en' },
        { label: 'German', value: 'de' },
        { label: 'Polish', value: 'pl' },
        { label: 'Norwegian', value: 'nb' },
        { label: 'Danish', value: 'da' }
    ];

    public static get initialLanguageData(): I18NText {
        return I18n.allLanguageOptions.reduce(
            (acc, current) => ({ ...acc, [current.value]: '' }),
            {} as I18NText
        );
    }

    static i18nTextToStringConverter(text: I18NText): string {
        return text[I18n.mainLanguageKey] || '';
    }

    static textToI18nTextConverter(
        i18nValue?: I18NText,
        legacyValue?: string
    ): I18NText {
        return (
            i18nValue || {
                ...I18n.initialLanguageData,
                ...(legacyValue
                    ? {
                          [I18n.mainLanguageKey]: legacyValue
                      }
                    : {})
            }
        );
    }

    static textToMainI18nTextConverter(text: string) {
        return {
            [I18n.mainLanguageKey]: text
        };
    }

    static get emptyI18nText(): I18NText {
        return {
            en: '',
            de: '',
            pl: '',
            da: '',
            nb: ''
        };
    }

    public formatShortMonth(date?: LocalDate | ZonedDateTime) {
        if (!date) {
            return '-';
        }

        const formatter = new Intl.DateTimeFormat(this.currentLocale, {
            month: 'short'
        });

        return formatter.format(date.toDate());
    }
}
